import React, { useContext, useEffect, useMemo, useState, useRef } from "react";
import { Form, Spinner } from "react-bootstrap";
import { DateRangePicker } from "rsuite";
import "rsuite/dist/rsuite.min.css";
import slugify from "slugify";
import StarekContext from "../../context/StarekContext";
import { longDateString } from "../../utils";
import {
    SidebarView,
    SidebarContext,
    SidebarContextProvider,
    useSyncedField,
} from "../../components/Sidebar";

const toLocalInput = (d) => {
    const pad = (n) => String(n).padStart(2, "0");
    const dt = new Date(d);
    const yyyy = dt.getFullYear();
    const mm = pad(dt.getMonth() + 1);
    const dd = pad(dt.getDate());
    const hh = pad(dt.getHours());
    const min = pad(dt.getMinutes());
    return `${yyyy}-${mm}-${dd}T${hh}:${min}`;
};

const normalizeToLocalInput = (val) => {
    if (!val) return "";
    if (/^\d{4}-\d{2}-\d{2}T\d{2}:\d{2}$/.test(val)) return val;
    const d = new Date(val);
    return isNaN(d.getTime()) ? String(val).slice(0, 16) : toLocalInput(d);
};

// Page component for maintenance basic details
function MaintenanceBasicDetails({ devices: devicesProp }) {
    const starekContext = useContext(StarekContext);
    const { entity } = useContext(SidebarContext);

    const [scheduleName, setScheduleName] = useSyncedField("name", "");
    const [scheduleDescription, setScheduleDescription] = useSyncedField(
        "description",
        "",
    );
    const [selectedDeviceId, setSelectedDeviceId] = useSyncedField(
        "deviceId",
        "",
    );
    const [dateRange, setDateRange] = useSyncedField("dateRange", [
        new Date(),
        new Date(),
    ]);

    const [devices, setDevices] = useState(devicesProp || []);
    const [loadingDevices, setLoadingDevices] = useState(false);
    const userModifiedNameRef = useRef(false);
    const userModifiedDescriptionRef = useRef(false);
    const entityIdRef = useRef(null);
    const lastGeneratedNameRef = useRef("");
    const lastGeneratedDescriptionRef = useRef("");

    const lastUsedPlant = starekContext?.lastUsedPlant;
    const lastUsedWorkspace = starekContext?.lastUsedWorkspace;
    const token = starekContext?.auth;
    const { host, scheme, port } = lastUsedWorkspace?.engine || {};
    const apiUrl =
        lastUsedWorkspace && scheme && host && port
            ? `${scheme}://${host}:${port}/api/v1/${lastUsedWorkspace.name}/graphql`
            : null;

    // Initialize fields from entity when it changes
    useEffect(() => {
        const currentEntityId = entity?.id || null;

        // Only reinitialize if the entity ID actually changed
        if (
            entityIdRef.current === currentEntityId &&
            currentEntityId !== null
        ) {
            return;
        }
        entityIdRef.current = currentEntityId;

        if (entity) {
            let attrs = entity?.schedule_attributes;
            if (typeof attrs === "string") {
                try {
                    attrs = JSON.parse(attrs);
                } catch {
                    attrs = {};
                }
            }
            const from = attrs?.from ? new Date(attrs.from) : new Date();
            const to = attrs?.to ? new Date(attrs.to) : from;
            setScheduleName(entity?.name || "");
            setScheduleDescription(entity?.description || "");
            setSelectedDeviceId(entity?.device_id ?? "");
            setDateRange([from, to]);
            userModifiedNameRef.current = false;
            userModifiedDescriptionRef.current = false;
            lastGeneratedNameRef.current = "";
            lastGeneratedDescriptionRef.current = "";
        } else {
            const now = new Date();
            setScheduleName("");
            setScheduleDescription("");
            setSelectedDeviceId("");
            setDateRange([now, now]);
            userModifiedNameRef.current = false;
            userModifiedDescriptionRef.current = false;
            lastGeneratedNameRef.current = "";
            lastGeneratedDescriptionRef.current = "";
        }

        // Fetch devices every time sidebar opens (unless devices are provided as prop)
        if (!devicesProp?.length) {
            fetchDevices();
        }
    }, [entity?.id]);

    // Helper function to create a hash from dates
    const hashDates = (start, end) => {
        const s = start instanceof Date ? start.getTime() : 0;
        const e = end instanceof Date ? end.getTime() : 0;
        const combined = s + e;
        return Math.abs(combined).toString(36).slice(0, 8);
    };

    // Auto-generate description and name when device or dates change (but not if user modified them)
    useEffect(() => {
        // Only auto-generate for new entities (not editing existing ones)
        if (entity?.id) return;

        // Only run if we have devices loaded
        if (devices.length === 0) return;

        const selectedDevice = devices.find((d) => d.id === selectedDeviceId);
        const [start, end] = dateRange;

        // Auto-generate description if user hasn't modified it
        if (!userModifiedDescriptionRef.current) {
            let generatedDescription = "Scheduled Maintenance";
            if (selectedDevice) {
                generatedDescription += ` on ${selectedDevice.name || selectedDevice.id}`;
            }
            if (start && end) {
                const startDate = longDateString(start);
                const endDate = longDateString(end);
                generatedDescription += ` from ${startDate} to ${endDate}`;
            }
            // Only update if the generated value is different from the last one
            if (generatedDescription !== lastGeneratedDescriptionRef.current) {
                lastGeneratedDescriptionRef.current = generatedDescription;
                setScheduleDescription(generatedDescription);
            }
        }

        // Auto-generate slug name if user hasn't modified it
        if (!userModifiedNameRef.current) {
            let generatedName = "";
            if (selectedDevice) {
                generatedName += slugify(
                    selectedDevice.name || selectedDevice.id,
                    { lower: true, strict: true },
                );
            }
            if (!generatedName) {
                generatedName = "maintenance";
            }
            if (start && end) {
                const dateHash = hashDates(start, end);
                generatedName += `-${dateHash}`;
            }
            // Only update if the generated value is different from the last one
            if (generatedName !== lastGeneratedNameRef.current) {
                lastGeneratedNameRef.current = generatedName;
                setScheduleName(generatedName);
            }
        }
    }, [selectedDeviceId, dateRange, entity?.id]);

    const fetchDevices = async () => {
        if (devicesProp?.length || loadingDevices) return;
        if (!apiUrl || !token || !lastUsedPlant?.id) return;

        setLoadingDevices(true);
        try {
            const q1 = {
                query: `
          { shadowDevices(where: { plant_id: { equals: ${lastUsedPlant.id} } }) { id name } }
        `,
            };
            let res = await fetch(apiUrl, {
                method: "POST",
                headers: {
                    "Content-Type": "application/json",
                    Authorization: `Bearer ${token}`,
                },
                body: JSON.stringify(q1),
            });
            let json = await res.json();
            let list = json?.data?.shadowDevices || [];

            if (!Array.isArray(list) || list.length === 0) {
                const q2 = {
                    query: `
            { devices(where: { plant_id: { equals: ${lastUsedPlant.id} } }) { id name } }
          `,
                };
                res = await fetch(apiUrl, {
                    method: "POST",
                    headers: {
                        "Content-Type": "application/json",
                        Authorization: `Bearer ${token}`,
                    },
                    body: JSON.stringify(q2),
                });
                json = await res.json();
                list = json?.data?.devices || [];
            }
            setDevices(list);
        } catch (e) {
            console.error("Error fetching devices:", e);
            setDevices([]);
        } finally {
            setLoadingDevices(false);
        }
    };

    return (
        <Form className="tw-flex tw-flex-col tw-gap-4">
            <div>
                <label className="tw-text-sm tw-font-medium">
                    Name <span className="tw-text-red-500">*</span>
                </label>
                <input
                    type="text"
                    className="tw-w-full tw-border tw-rounded-md tw-p-2"
                    placeholder="Auto-generated slug"
                    value={scheduleName}
                    onChange={(e) => {
                        setScheduleName(e.target.value);
                        userModifiedNameRef.current = true;
                    }}
                />
            </div>

            <div>
                <label className="tw-text-sm tw-font-medium tw-flex tw-items-center tw-gap-2">
                    Select Device
                    {loadingDevices && <Spinner animation="border" size="sm" />}
                </label>
                <select
                    className="tw-w-full tw-border tw-rounded-md tw-p-2"
                    value={selectedDeviceId}
                    onChange={(e) => setSelectedDeviceId(e.target.value)}
                    disabled={loadingDevices}
                >
                    <option value="">Select device</option>
                    {devices?.map((d) => (
                        <option key={d.id} value={d.id}>
                            {d.name || d.id}
                        </option>
                    ))}
                </select>
            </div>

            <div className="tw-flex tw-flex-col">
                <label className="tw-text-sm tw-text-gray-600 tw-mb-1">
                    Date Range
                </label>
                <DateRangePicker
                    value={dateRange}
                    onChange={(value) =>
                        setDateRange(value || [new Date(), new Date()])
                    }
                    format="yyyy-MM-dd HH:mm"
                    placeholder="Select Date Range"
                    style={{ width: "100%" }}
                    shouldDisableDate={(date) => date < new Date()}
                />
            </div>

            <div>
                <label className="tw-text-sm tw-font-medium">Description</label>
                <textarea
                    className="tw-w-full tw-border tw-rounded-md tw-p-2"
                    rows="3"
                    placeholder="Auto-generated from device and dates"
                    value={scheduleDescription}
                    onChange={(e) => {
                        setScheduleDescription(e.target.value);
                        userModifiedDescriptionRef.current = true;
                    }}
                />
            </div>
        </Form>
    );
}

// Wrapper component that provides the context
const MaintenanceScheduleSidebar = (props) => {
    const {
        show,
        onDiscard,
        item = null,
        plantId,
        devices: devicesProp = null,
        onSave = async () => {},
        onDelete = async () => {},
    } = props;
    const starekContext = useContext(StarekContext);

    const handleSaveInternal = async (emittedData, entity) => {
        try {
            const scheduleName = emittedData.name?.trim();
            if (!scheduleName) {
                starekContext.showError("Schedule name is required");
                throw new Error("Schedule name is required");
            }
            if (!plantId) {
                starekContext.showError("Plant ID is required");
                throw new Error("Plant ID is required");
            }

            if (!emittedData.deviceId) {
                starekContext.showError("Please select a device");
                throw new Error("Please select a device");
            }

            if (!emittedData.dateRange || emittedData.dateRange.length !== 2) {
                starekContext.showError("Please select a date range");
                throw new Error("Please select a date range");
            }

            const [from, to] = emittedData.dateRange;
            const now = new Date();
            if (!from || !to || isNaN(from.getTime()) || isNaN(to.getTime())) {
                starekContext.showError("Invalid date/time");
                throw new Error("Invalid date/time");
            }
            if (from <= now) {
                starekContext.showError("Start time must be in the future");
                throw new Error("Start time must be in the future");
            }
            if (to <= from) {
                starekContext.showError("End time must be after Start time");
                throw new Error("End time must be after Start time");
            }

            const payload = {
                id: entity?.id,
                deviceId: emittedData.deviceId,
                from: from.toISOString(),
                to: to.toISOString(),
                name: scheduleName,
                description: emittedData.description || "",
            };

            await onSave(payload);

            starekContext.showSuccess(
                entity?.id
                    ? `Maintenance Schedule "${scheduleName}" updated successfully`
                    : `Maintenance Schedule "${scheduleName}" created successfully`,
            );
        } catch (error) {
            console.error("Error saving maintenance schedule:", error);
            // Don't show error again if we already showed it during validation
            if (
                !error.message.includes("select") &&
                !error.message.includes("required") &&
                !error.message.includes("date") &&
                !error.message.includes("time")
            ) {
                starekContext.showError(
                    error?.message ||
                        "Failed to save maintenance schedule. Please try again.",
                );
            }
            throw error;
        }
    };

    return (
        <SidebarContextProvider
            entity={item}
            show={show}
            onSave={handleSaveInternal}
            onDelete={onDelete}
            onDiscard={onDiscard}
        >
            <SidebarView entityName="Maintenance Schedule">
                <SidebarView.Page
                    name="maintenance_details"
                    title="Basic Details"
                >
                    <MaintenanceBasicDetails devices={devicesProp} />
                </SidebarView.Page>
            </SidebarView>
        </SidebarContextProvider>
    );
};

export default MaintenanceScheduleSidebar;
